import { formatMinutes, parseDurationToMinutes } from '@jira/shared';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  Bug,
  Calendar,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  CircleDot,
  Clock,
  ExternalLink,
  FileText,
  Flag,
  Flame,
  Hash,
  Hourglass,
  Layers,
  MessageSquare,
  Minus,
  Paperclip,
  Pencil,
  Plus,
  Send,
  Smile,
  Target,
  Trash2,
  Upload,
  User,
  UserCircle2,
  X,
} from 'lucide-react';
import * as React from 'react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { AuthenticatedImage } from '@/components/common/authenticated-image';
import { Markdown } from '@/components/common/markdown';
import { MarkdownEditor } from '@/components/common/markdown-editor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { api, extractApiErrorMessage } from '@/lib/api';
import {
  createComment,
  createTimeLog,
  deleteCommentApi,
  listComments,
  listTimeLogs,
  toggleReactionApi,
  updateCommentBody,
  uploadFile,
  type CommentRow,
  type TimeLogRow,
} from '@/lib/collab-api';
import { listMembers } from '@/lib/org-api';
import {
  getIssue,
  listStatuses,
  updateIssue,
  type IssueDetail,
  type IssuePriority,
  type IssueType,
  type StatusCategory,
} from '@/lib/project-api';
import { listSprints } from '@/lib/sprint-api';
import { useIssueDrawerStore } from '@/stores/issue-drawer-store';
import { useAuthStore } from '@/stores/auth-store';
import { cn, initialsFrom } from '@/lib/utils';

const TYPE_META: Record<
  IssueType,
  { label: string; icon: typeof FileText; iconColor: string; bg: string }
> = {
  STORY: {
    label: 'Story',
    icon: FileText,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-500/10 ring-emerald-500/20',
  },
  TASK: {
    label: 'Task',
    icon: CheckSquare,
    iconColor: 'text-sky-600 dark:text-sky-400',
    bg: 'bg-sky-500/10 ring-sky-500/20',
  },
  BUG: {
    label: 'Bug',
    icon: Bug,
    iconColor: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-500/10 ring-rose-500/20',
  },
  SUBTASK: {
    label: 'Subtask',
    icon: CircleDot,
    iconColor: 'text-slate-500 dark:text-slate-400',
    bg: 'bg-muted ring-border',
  },
};

const PRIORITY_META: Record<
  IssuePriority,
  { label: string; icon: typeof ArrowUp; color: string }
> = {
  LOWEST: { label: 'Lowest', icon: ArrowDown, color: 'text-slate-400' },
  LOW: { label: 'Low', icon: ArrowDown, color: 'text-emerald-500' },
  MEDIUM: { label: 'Medium', icon: Minus, color: 'text-amber-500' },
  HIGH: { label: 'High', icon: ArrowUp, color: 'text-orange-500' },
  HIGHEST: { label: 'Highest', icon: Flame, color: 'text-rose-500' },
};

const STATUS_CATEGORY_CLASS: Record<StatusCategory, string> = {
  TODO: 'bg-slate-500/10 text-slate-700 ring-slate-500/30 dark:text-slate-300',
  IN_PROGRESS: 'bg-sky-500/10 text-sky-700 ring-sky-500/30 dark:text-sky-300',
  DONE: 'bg-emerald-500/10 text-emerald-700 ring-emerald-500/30 dark:text-emerald-300',
};

const PRIORITY_OPTIONS: IssuePriority[] = ['LOWEST', 'LOW', 'MEDIUM', 'HIGH', 'HIGHEST'];

function renderUserOption(
  user: { name: string; email?: string | null } | null,
): React.ReactNode {
  if (!user) {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted ring-1 ring-border">
          <User className="h-3 w-3" aria-hidden />
        </span>
        <span className="italic">Unassigned</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 text-sm">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold uppercase text-primary ring-1 ring-primary/20">
        {initialsFrom(user.name, user.email ?? '')}
      </span>
      <span className="truncate">{user.name}</span>
    </span>
  );
}

function renderPriorityOption(p: IssuePriority): React.ReactNode {
  const meta = PRIORITY_META[p];
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <Icon className={cn('h-4 w-4', meta.color)} aria-hidden />
      {meta.label}
    </span>
  );
}
const REACTIONS = [':+1:', ':-1:', ':tada:', ':eyes:'];
const REACTION_DISPLAY: Record<string, string> = {
  ':+1:': '👍',
  ':-1:': '👎',
  ':tada:': '🎉',
  ':eyes:': '👀',
};

export function IssueDrawer() {
  const target = useIssueDrawerStore((s) => s.target);
  const close = useIssueDrawerStore((s) => s.close);
  const open = target !== null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => (!next ? close() : null)}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/60 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed right-0 top-0 z-50 flex h-full w-full max-w-2xl flex-col border-l bg-background shadow-2xl outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:slide-in-from-right-full data-[state=closed]:slide-out-to-right-full',
            'data-[state=open]:duration-300 data-[state=closed]:duration-200',
          )}
          aria-describedby={undefined}
        >
          {target ? <IssueDrawerBody target={target} /> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function IssueDrawerBody({
  target,
}: {
  target: {
    orgId: string;
    orgSlug: string;
    projectKey: string;
    issueKey: string;
  };
}) {
  const { orgId, orgSlug, projectKey, issueKey } = target;
  const qc = useQueryClient();
  const { error } = useToast();
  const close = useIssueDrawerStore((s) => s.close);

  const user = useAuthStore((s) => s.user);
  const membership = user?.memberships.find((m) => m.organizationId === orgId);
  const canEdit = Boolean(user?.isSuperAdmin || membership?.permissions.includes('issue.edit'));
  const canComment = Boolean(
    user?.isSuperAdmin || membership?.permissions.includes('issue.comment'),
  );
  const canLogTime = Boolean(
    user?.isSuperAdmin || membership?.permissions.includes('issue.log_time'),
  );

  const issueQuery = useQuery({
    queryKey: ['org', orgId, 'issue', issueKey],
    queryFn: () => getIssue(orgId, issueKey),
  });

  const issue = issueQuery.data ?? null;

  const statusesQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey, 'statuses'],
    queryFn: () => (issue ? listStatuses(orgId, issue.project.id) : Promise.resolve([])),
    enabled: Boolean(issue),
  });

  const membersQuery = useQuery({
    queryKey: ['org', orgId, 'members', 'compact'],
    queryFn: () => listMembers(orgId, { limit: 100 }),
  });

  const sprintsQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey, 'sprints', 'compact'],
    queryFn: () => (issue ? listSprints(orgId, issue.project.id) : Promise.resolve([])),
    enabled: Boolean(issue),
  });

  const patch = useMutation({
    mutationFn: (body: Parameters<typeof updateIssue>[2]) => updateIssue(orgId, issueKey, body),
    onSuccess: (next) => {
      qc.setQueryData(['org', orgId, 'issue', issueKey], next);
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'board'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'backlog'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'issues'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'recent-issues'] });
    },
    onError: (e) => error('Could not update issue', extractApiErrorMessage(e)),
  });

  if (issueQuery.isLoading || !issue) {
    return (
      <>
        <DrawerHeader issueKey={issueKey} onClose={close} />
        <div className="flex-1 space-y-4 overflow-y-auto p-6">
          <Skeleton className="h-7 w-3/4" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </>
    );
  }

  if (issueQuery.isError) {
    return (
      <>
        <DrawerHeader issueKey={issueKey} onClose={close} />
        <div className="flex-1 p-6">
          <p className="text-sm text-rose-600">{extractApiErrorMessage(issueQuery.error)}</p>
        </div>
      </>
    );
  }

  const typeMeta = TYPE_META[issue.type];
  const TypeIconCmp = typeMeta.icon;
  const statuses = statusesQuery.data ?? [];
  const members = membersQuery.data?.items ?? [];
  const sprints = sprintsQuery.data ?? [];

  return (
    <>
      <DrawerHeader
        issueKey={issue.key}
        onClose={close}
        issue={issue}
        orgSlug={orgSlug}
        projectKey={projectKey}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="space-y-6 px-6 py-5">
          {/* Title */}
          <TitleSection
            issue={issue}
            typeIcon={TypeIconCmp}
            typeMeta={typeMeta}
            canEdit={canEdit}
            onSave={(title) => patch.mutate({ title })}
          />

          {/* Description */}
          <DescriptionSection
            issue={issue}
            orgId={orgId}
            canEdit={canEdit}
            onSave={(body) => patch.mutate({ descriptionMarkdown: body })}
          />

          {/* Attachments */}
          <AttachmentsSection issue={issue} orgId={orgId} projectKey={projectKey} />

          {/* Subtasks (if any) */}
          {issue.subtasks.length > 0 ? (
            <SubtasksSection
              issue={issue}
              orgId={orgId}
              orgSlug={orgSlug}
              projectKey={projectKey}
            />
          ) : null}

          {/* Details */}
          <DetailsSection
            issue={issue}
            statuses={statuses}
            members={members}
            sprints={sprints}
            canEdit={canEdit}
            canLogTime={canLogTime}
            orgId={orgId}
            onPatch={(body) => patch.mutate(body)}
          />

          {/* Comments */}
          <ActivitySection
            issue={issue}
            orgId={orgId}
            canComment={canComment}
            currentUserId={user?.id ?? null}
          />
        </div>
      </div>
    </>
  );
}

// -------------------- Header --------------------

function DrawerHeader({
  issueKey,
  onClose,
  issue,
  orgSlug,
  projectKey,
}: {
  issueKey: string;
  onClose: () => void;
  issue?: IssueDetail;
  orgSlug?: string;
  projectKey?: string;
}) {
  const accentCls = issue
    ? STATUS_CATEGORY_CLASS[issue.statusCategory]
    : 'bg-muted text-muted-foreground ring-border';
  const typeMeta = issue ? TYPE_META[issue.type] : null;
  const TypeIconCmp = typeMeta?.icon;
  return (
    <div className="relative border-b bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 py-3.5">
      <div className="flex items-center gap-2">
        {issue && typeMeta && TypeIconCmp ? (
          <div
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-md ring-1',
              typeMeta.bg,
            )}
            title={typeMeta.label}
          >
            <TypeIconCmp className={cn('h-3 w-3', typeMeta.iconColor)} aria-hidden />
          </div>
        ) : null}
        <span className="inline-flex items-center gap-1.5 rounded-md bg-background px-2 py-1 font-mono text-xs font-semibold text-primary ring-1 ring-border">
          {issueKey}
        </span>
        {issue ? (
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
              accentCls,
            )}
          >
            {issue.statusName}
          </span>
        ) : (
          <Skeleton className="h-5 w-24 rounded-full" />
        )}
        <div className="ml-auto flex items-center gap-1">
          {issue && orgSlug && projectKey ? (
            <Link
              to={`/o/${orgSlug}/projects/${projectKey}/issues/${issue.key}`}
              onClick={onClose}
              className="inline-flex h-8 items-center justify-center rounded-md px-2 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
              title="Open full view"
              aria-label="Open full view"
            >
              <ExternalLink className="h-4 w-4" aria-hidden />
            </Link>
          ) : null}
          <DialogPrimitive.Close asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label="Close drawer">
              <X className="h-4 w-4" aria-hidden />
            </Button>
          </DialogPrimitive.Close>
        </div>
      </div>
      <DialogPrimitive.Title className="sr-only">Issue {issueKey}</DialogPrimitive.Title>
    </div>
  );
}

// -------------------- Title section --------------------

function TitleSection({
  issue,
  typeIcon: TypeIconCmp,
  typeMeta,
  canEdit,
  onSave,
}: {
  issue: IssueDetail;
  typeIcon: typeof FileText;
  typeMeta: { label: string; iconColor: string; bg: string };
  canEdit: boolean;
  onSave: (title: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(issue.title);

  React.useEffect(() => {
    setDraft(issue.title);
  }, [issue.title]);

  const commit = () => {
    const next = draft.trim();
    if (next && next !== issue.title) onSave(next);
    setEditing(false);
  };

  return (
    <div className="flex items-start gap-3">
      <div
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1',
          typeMeta.bg,
        )}
        title={typeMeta.label}
      >
        <TypeIconCmp className={cn('h-4 w-4', typeMeta.iconColor)} aria-hidden />
      </div>
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setDraft(issue.title);
              setEditing(false);
            }
          }}
          maxLength={200}
          className="min-w-0 flex-1 rounded-md border border-primary/50 bg-background px-2 py-1 text-xl font-semibold leading-snug tracking-tight outline-none focus:ring-2 focus:ring-ring"
        />
      ) : (
        <h2
          role={canEdit ? 'button' : undefined}
          tabIndex={canEdit ? 0 : undefined}
          onClick={() => (canEdit ? setEditing(true) : null)}
          onKeyDown={(e) => {
            if (!canEdit) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setEditing(true);
            }
          }}
          className={cn(
            'min-w-0 flex-1 rounded-md px-2 py-1 text-xl font-semibold leading-snug tracking-tight transition-colors',
            canEdit ? 'cursor-text hover:bg-muted/60' : '',
          )}
          title={canEdit ? 'Click to edit' : undefined}
        >
          {issue.title}
        </h2>
      )}
    </div>
  );
}

// -------------------- Description --------------------

function DescriptionSection({
  issue,
  orgId,
  canEdit,
  onSave,
}: {
  issue: IssueDetail;
  orgId: string;
  canEdit: boolean;
  onSave: (body: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(issue.descriptionMarkdown ?? '');

  if (editing) {
    return (
      <section className="space-y-2">
        <SectionHeading icon={FileText} label="Description" />
        {/* MarkdownEditor supports drag-drop + paste to upload images; uploads
            are attached to this issue (via issueId) so they show up in the
            Attachments section too. */}
        <MarkdownEditor
          value={draft}
          onChange={setDraft}
          orgId={orgId}
          issueId={issue.id}
          projectId={issue.project.id}
          placeholder="Add a description… Markdown supported. Paste or drop images to attach."
          minHeight={200}
        />
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setEditing(false);
              setDraft(issue.descriptionMarkdown ?? '');
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              onSave(draft.trim() === '' ? null : draft);
              setEditing(false);
            }}
          >
            Save
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-2">
      <SectionHeading icon={FileText} label="Description" />
      <button
        type="button"
        onClick={() => (canEdit ? setEditing(true) : null)}
        disabled={!canEdit}
        className={cn(
          'w-full rounded-lg border bg-background p-4 text-left transition-colors',
          canEdit ? 'hover:border-primary/40 hover:bg-muted/30' : 'cursor-default',
        )}
      >
        {issue.descriptionMarkdown ? (
          <Markdown
            source={issue.descriptionMarkdown}
            className="prose-sm max-w-none dark:prose-invert"
          />
        ) : (
          <p className="text-sm italic text-muted-foreground">
            {canEdit ? 'Add a description…' : 'No description yet.'}
          </p>
        )}
      </button>
    </section>
  );
}

// -------------------- Attachments --------------------

function AttachmentsSection({
  issue,
  orgId,
  projectKey,
}: {
  issue: IssueDetail;
  orgId: string;
  projectKey: string;
}) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', issue.key] });
    qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', issue.id] });
    qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'issues'] });
  };

  const upload = async (file: File) => {
    if (uploading) return;
    setUploading(true);
    setProgress(0);
    try {
      await uploadFile(
        orgId,
        file,
        { issueId: issue.id, projectId: issue.project.id },
        (pct) => setProgress(pct),
      );
      success('Attachment uploaded', file.name);
      invalidate();
    } catch (err) {
      error('Upload failed', extractApiErrorMessage(err));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handlePick = () => fileInputRef.current?.click();

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    for (const file of Array.from(files)) {
      // eslint-disable-next-line no-await-in-loop
      await upload(file);
    }
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <SectionHeading icon={Paperclip} label="Attachments" />
        {issue.attachments.length > 0 ? (
          <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
            {issue.attachments.length}
          </span>
        ) : null}
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          'rounded-lg border border-dashed bg-muted/10 p-3 transition-colors',
          dragOver ? 'border-primary/60 bg-primary/5' : 'hover:border-primary/40',
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="sr-only"
          onChange={(e) => {
            void handleFiles(e.target.files);
            // reset so selecting the same file twice still fires change
            e.target.value = '';
          }}
        />

        {issue.attachments.length > 0 ? (
          <ul className="mb-2 grid gap-2 sm:grid-cols-2">
            {issue.attachments.map((att) => (
              <li key={att.id}>
                <AttachmentCard attachment={att} />
              </li>
            ))}
          </ul>
        ) : null}

        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] text-muted-foreground">
            Drag & drop files here, or
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handlePick}
            disabled={uploading}
            className="gap-1.5"
          >
            {uploading ? (
              <>
                <CircleDashed className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Uploading… {progress}%
              </>
            ) : (
              <>
                <Upload className="h-3.5 w-3.5" aria-hidden /> Add file
              </>
            )}
          </Button>
        </div>
      </div>
    </section>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type AttachmentLite = {
  id: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
};

function AttachmentCard({ attachment }: { attachment: AttachmentLite }) {
  const isImage = attachment.mimeType.startsWith('image/');
  const { error } = useToast();
  const [downloading, setDownloading] = useState(false);

  const onOpen = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      // Use the authenticated axios instance so the Bearer token is attached.
      const res = await api.get(`/uploads/${attachment.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.originalFilename;
      a.rel = 'noreferrer';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revoke so the browser has a chance to kick off the download.
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      error('Could not download attachment', extractApiErrorMessage(e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={downloading}
      className="group flex w-full items-center gap-2 rounded-md border bg-background p-2 text-left text-sm shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md disabled:opacity-60"
      title={`${attachment.originalFilename} · ${formatFileSize(attachment.sizeBytes)}`}
    >
      {isImage ? (
        <AuthenticatedImage
          attachmentId={attachment.id}
          alt={attachment.originalFilename}
          variant="thumbnail"
          className="h-10 w-10 shrink-0 rounded-md border object-cover"
          fallbackClassName="h-10 w-10 shrink-0 rounded-md border"
        />
      ) : (
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground ring-1 ring-border">
          <Paperclip className="h-4 w-4" aria-hidden />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-foreground group-hover:text-primary">
          {attachment.originalFilename}
        </div>
        <div className="text-[10px] text-muted-foreground">
          {formatFileSize(attachment.sizeBytes)} ·{' '}
          {new Date(attachment.createdAt).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
          })}
        </div>
      </div>
      {downloading ? (
        <CircleDashed className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" aria-hidden />
      ) : null}
    </button>
  );
}


// -------------------- Subtasks --------------------

function SubtasksSection({
  issue,
  orgId,
  orgSlug,
  projectKey,
}: {
  issue: IssueDetail;
  orgId: string;
  orgSlug: string;
  projectKey: string;
}) {
  const openIssue = useIssueDrawerStore((s) => s.open);
  const doneCount = issue.subtasks.filter((s) => s.statusCategory === 'DONE').length;
  const total = issue.subtasks.length;
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <SectionHeading icon={CircleDashed} label={`Subtasks`} />
        <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
          {doneCount}/{total} done
        </span>
      </div>
      {/* Progress bar */}
      <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-emerald-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <ul className="space-y-1.5">
        {issue.subtasks.map((sub) => {
          const subTypeMeta = TYPE_META[sub.type];
          const SubTypeIcon = subTypeMeta.icon;
          return (
            <li key={sub.id}>
              <button
                type="button"
                onClick={() => openIssue({ orgId, orgSlug, projectKey, issueKey: sub.key })}
                className="flex w-full items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-left text-sm transition-colors hover:border-primary/40 hover:bg-primary/5"
              >
                <div
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-md ring-1',
                    subTypeMeta.bg,
                  )}
                >
                  <SubTypeIcon className={cn('h-3 w-3', subTypeMeta.iconColor)} aria-hidden />
                </div>
                <span className="font-mono text-[11px] font-medium text-primary">{sub.key}</span>
                <span className="min-w-0 flex-1 truncate">{sub.title}</span>
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide ring-1',
                    STATUS_CATEGORY_CLASS[sub.statusCategory],
                  )}
                >
                  {sub.statusName}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// -------------------- Details (collapsible Jira-style card) --------------------

function DetailsSection({
  issue,
  statuses,
  members,
  sprints,
  canEdit,
  canLogTime,
  orgId,
  onPatch,
}: {
  issue: IssueDetail;
  statuses: Array<{ id: string; name: string; category: StatusCategory }>;
  members: Array<{ userId: string; name: string }>;
  sprints: Array<{ id: string; name: string; state: string }>;
  canEdit: boolean;
  canLogTime: boolean;
  orgId: string;
  onPatch: (body: Parameters<typeof updateIssue>[2]) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const reporterInitials = issue.reporter
    ? initialsFrom(issue.reporter.name, issue.reporter.email)
    : '';
  const created = new Date(issue.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const updated = new Date(issue.updatedAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <section className="overflow-hidden rounded-xl border bg-background">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 bg-muted/30 px-4 py-2.5 text-left transition-colors hover:bg-muted/50"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Layers className="h-3.5 w-3.5" aria-hidden /> Details
        </div>
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
        )}
      </button>

      {expanded ? (
        <div className="divide-y">
          {/* Time tracking */}
          <TimeTrackingRow issue={issue} orgId={orgId} canLogTime={canLogTime} />

          {/* Assignee */}
          <DetailRow label="Assignee" icon={UserCircle2}>
            {canEdit ? (
              <Select
                variant="inline"
                value={issue.assigneeId ?? ''}
                onChange={(e) => onPatch({ assigneeId: e.target.value ? e.target.value : null })}
                wrapperClassName="max-w-[18rem]"
                className="text-sm"
              >
                <option value="">{renderUserOption(null)}</option>
                {members.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {renderUserOption({ name: m.name })}
                  </option>
                ))}
              </Select>
            ) : issue.assignee ? (
              renderUserOption({ name: issue.assignee.name, email: issue.assignee.email })
            ) : (
              renderUserOption(null)
            )}
          </DetailRow>

          {/* Sprint */}
          <DetailRow label="Sprint" icon={Target}>
            {canEdit ? (
              <Select
                variant="inline"
                value={issue.sprintId ?? ''}
                onChange={(e) => onPatch({ sprintId: e.target.value ? e.target.value : null })}
                wrapperClassName="max-w-[18rem]"
                className="text-sm"
              >
                <option value="">
                  <span className="italic text-muted-foreground">Backlog</span>
                </option>
                {sprints.map((s) => (
                  <option key={s.id} value={s.id}>
                    <span className="inline-flex items-center gap-1.5">
                      <Target className="h-3.5 w-3.5 text-primary" aria-hidden />
                      {s.name}
                      {s.state === 'ACTIVE' ? (
                        <span className="ml-1 rounded-full bg-emerald-500/10 px-1.5 py-0 text-[9px] font-semibold uppercase text-emerald-700 ring-1 ring-emerald-500/30 dark:text-emerald-300">
                          Active
                        </span>
                      ) : s.state === 'COMPLETED' ? (
                        <span className="ml-1 text-[10px] text-muted-foreground">Done</span>
                      ) : null}
                    </span>
                  </option>
                ))}
              </Select>
            ) : issue.sprintId ? (
              (() => {
                const s = sprints.find((x) => x.id === issue.sprintId);
                return (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 text-sm">
                    <Target className="h-4 w-4 text-primary" aria-hidden />
                    {s?.name ?? 'Sprint'}
                  </span>
                );
              })()
            ) : (
              <span className="px-2 py-1 text-sm italic text-muted-foreground">Backlog</span>
            )}
          </DetailRow>

          {/* Priority */}
          <DetailRow label="Priority" icon={Flame}>
            {canEdit ? (
              <Select
                variant="inline"
                value={issue.priority}
                onChange={(e) => onPatch({ priority: e.target.value as IssuePriority })}
                wrapperClassName="max-w-[14rem]"
                className="text-sm"
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {renderPriorityOption(p)}
                  </option>
                ))}
              </Select>
            ) : (
              <span className="px-2 py-1">{renderPriorityOption(issue.priority)}</span>
            )}
          </DetailRow>

          {/* Status */}
          <DetailRow label="Status" icon={CircleDot}>
            {canEdit ? (
              <Select
                variant="inline"
                value={issue.statusId}
                onChange={(e) => onPatch({ statusId: e.target.value })}
                wrapperClassName="max-w-[18rem]"
                className="text-sm"
              >
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ring-1',
                        STATUS_CATEGORY_CLASS[s.category],
                      )}
                    >
                      {s.name}
                    </span>
                  </option>
                ))}
              </Select>
            ) : (
              <span className="inline-flex px-2 py-1">
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ring-1',
                    STATUS_CATEGORY_CLASS[issue.statusCategory],
                  )}
                >
                  {issue.statusName}
                </span>
              </span>
            )}
          </DetailRow>

          {/* Story points */}
          <DetailRow label="Story points" icon={Hash}>
            {canEdit ? (
              <input
                type="number"
                min={0}
                max={999}
                defaultValue={issue.storyPoints ?? ''}
                placeholder="—"
                className="h-9 w-24 rounded-lg border border-input bg-background px-3 text-center text-sm font-medium tabular-nums shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  const next = v === '' ? null : Number(v);
                  if (next !== issue.storyPoints) onPatch({ storyPoints: next });
                }}
              />
            ) : issue.storyPoints != null ? (
              <span className="inline-flex h-7 min-w-[2rem] items-center justify-center rounded-full bg-muted px-2 text-sm font-semibold tabular-nums ring-1 ring-border">
                {issue.storyPoints}
              </span>
            ) : (
              <span className="text-sm italic text-muted-foreground">—</span>
            )}
          </DetailRow>

          {/* Reporter (read-only) */}
          <DetailRow label="Reporter" icon={Flag}>
            {issue.reporter ? (
              renderUserOption({ name: issue.reporter.name, email: issue.reporter.email })
            ) : (
              <span className="text-sm italic text-muted-foreground">—</span>
            )}
          </DetailRow>

          {/* Dates */}
          <div className="flex flex-wrap items-center gap-3 bg-muted/20 px-4 py-2 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" aria-hidden /> Created {created}
            </span>
            <span>·</span>
            <span>Updated {updated}</span>
          </div>
        </div>
      ) : null}
    </section>
  );
}

// -------------------- Time tracking row (embedded in Details) --------------------

function TimeTrackingRow({
  issue,
  orgId,
  canLogTime,
}: {
  issue: IssueDetail;
  orgId: string;
  canLogTime: boolean;
}) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [form, setForm] = useState(false);
  const [duration, setDuration] = useState('');
  const [loggedAt, setLoggedAt] = useState(() => toISODate(new Date()));
  const [description, setDescription] = useState('');

  const timelogsQuery = useQuery({
    queryKey: ['org', orgId, 'issue', issue.id, 'timelogs'],
    queryFn: () => listTimeLogs(orgId, issue.id),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', issue.id, 'timelogs'] });
    qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', issue.key] });
  };

  const create = useMutation({
    mutationFn: () =>
      createTimeLog(orgId, issue.id, {
        durationInput: duration.trim(),
        ...(loggedAt ? { loggedAt: new Date(loggedAt).toISOString() } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
      }),
    onSuccess: () => {
      success('Time logged');
      setDuration('');
      setDescription('');
      setForm(false);
      invalidate();
    },
    onError: (e) => error('Could not log time', extractApiErrorMessage(e)),
  });

  const parsePreview = (() => {
    const raw = duration.trim();
    if (!raw) return null;
    try {
      const minutes = parseDurationToMinutes(raw);
      return formatMinutes(minutes);
    } catch {
      return null;
    }
  })();

  const total = timelogsQuery.data?.totalMinutes ?? 0;
  const entries = timelogsQuery.data?.items ?? [];

  // Aggregate total minutes per user (so the panel shows who did how much,
  // without listing every individual entry).
  const byUser = (() => {
    const map = new Map<string, { name: string; minutes: number }>();
    for (const entry of entries) {
      const prev = map.get(entry.userId);
      map.set(entry.userId, {
        name: entry.userName,
        minutes: (prev?.minutes ?? 0) + entry.minutes,
      });
    }
    return Array.from(map.entries())
      .map(([userId, v]) => ({ userId, ...v }))
      .sort((a, b) => b.minutes - a.minutes);
  })();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!duration.trim() || !parsePreview || create.isPending) return;
    create.mutate();
  };

  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-muted text-muted-foreground ring-1 ring-border">
            <Hourglass className="h-3.5 w-3.5" aria-hidden />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Time tracking
            </div>
            <div className="text-sm font-semibold tabular-nums">
              {total > 0 ? formatMinutes(total) + ' logged' : 'No time logged'}
            </div>
          </div>
        </div>
        {canLogTime ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setForm((v) => !v)}
            className="gap-1.5"
          >
            {form ? <X className="h-3.5 w-3.5" aria-hidden /> : <Plus className="h-3.5 w-3.5" aria-hidden />}
            {form ? 'Cancel' : 'Log time'}
          </Button>
        ) : null}
      </div>

      {/* Per-user totals (not full log) */}
      {byUser.length > 0 ? (
        <ul className="mt-3 space-y-1.5">
          {byUser.map((u) => {
            const pct = total > 0 ? Math.round((u.minutes / total) * 100) : 0;
            return (
              <li
                key={u.userId}
                className="flex items-center gap-2 rounded-md bg-muted/30 px-2.5 py-1.5 text-xs"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold uppercase text-primary ring-1 ring-primary/20">
                  {initialsFrom(u.name, '')}
                </span>
                <span className="min-w-0 flex-1 truncate font-medium">{u.name}</span>
                {total > 0 ? (
                  <span className="text-[10px] tabular-nums text-muted-foreground">{pct}%</span>
                ) : null}
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-500/30 dark:text-emerald-300">
                  <Hourglass className="h-2.5 w-2.5" aria-hidden /> {formatMinutes(u.minutes)}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      {/* Log-time inline form */}
      {form && canLogTime ? (
        <form onSubmit={submit} className="mt-3 space-y-2 rounded-lg border bg-muted/20 p-3">
          <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
            <div>
              <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Hourglass className="h-3 w-3" aria-hidden /> Duration
              </div>
              <div className="relative">
                <Input
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="e.g. 1h 30m"
                  className="h-9 pr-24 text-sm"
                  autoFocus
                />
                {parsePreview ? (
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-500/30 dark:text-emerald-300">
                    = {parsePreview}
                  </span>
                ) : duration.trim() ? (
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-rose-500/10 px-2 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-rose-500/30 dark:text-rose-300">
                    invalid
                  </span>
                ) : null}
              </div>
            </div>
            <div>
              <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Calendar className="h-3 w-3" aria-hidden /> Date
              </div>
              <Input
                type="date"
                value={loggedAt}
                onChange={(e) => setLoggedAt(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
          </div>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Notes (optional)"
            className="h-9 text-sm"
            maxLength={500}
          />
          <div className="flex items-center justify-between pt-1">
            <p className="text-[10px] text-muted-foreground">
              Formats: <code>1h 30m</code>, <code>45m</code>, <code>1d</code>
            </p>
            <Button
              type="submit"
              size="sm"
              disabled={!duration.trim() || !parsePreview || create.isPending}
              className="gap-1.5"
            >
              {create.isPending ? (
                <>
                  <CircleDashed className="h-3.5 w-3.5 animate-spin" aria-hidden /> Logging…
                </>
              ) : (
                <>
                  <Clock className="h-3.5 w-3.5" aria-hidden /> Log
                </>
              )}
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}

// -------------------- Activity (tabs: All / Comments / History / Work log) --------------------

type ActivityTab = 'all' | 'comments' | 'history' | 'worklog';

type ActivityItem =
  | { kind: 'comment'; at: string; comment: CommentRow }
  | { kind: 'worklog'; at: string; entry: TimeLogRow }
  | { kind: 'history'; at: string; label: string };

function ActivitySection({
  issue,
  orgId,
  canComment,
  currentUserId,
}: {
  issue: IssueDetail;
  orgId: string;
  canComment: boolean;
  currentUserId: string | null;
}) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [tab, setTab] = useState<ActivityTab>('comments');
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);

  const commentsQuery = useQuery({
    queryKey: ['org', orgId, 'issue', issue.id, 'comments'],
    queryFn: () => listComments(orgId, issue.id),
  });

  const timelogsQuery = useQuery({
    queryKey: ['org', orgId, 'issue', issue.id, 'timelogs'],
    queryFn: () => listTimeLogs(orgId, issue.id),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', issue.id, 'comments'] });
    qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', issue.key] });
  };

  const create = useMutation({
    mutationFn: () => createComment(orgId, issue.id, draft.trim()),
    onSuccess: () => {
      success('Comment added');
      setDraft('');
      setComposerOpen(false);
      invalidate();
    },
    onError: (e) => error('Could not post comment', extractApiErrorMessage(e)),
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      updateCommentBody(orgId, id, body.trim()),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
    onError: (e) => error('Could not update comment', extractApiErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteCommentApi(orgId, id),
    onSuccess: () => {
      success('Comment deleted');
      invalidate();
    },
    onError: (e) => error('Could not delete comment', extractApiErrorMessage(e)),
  });

  const react = useMutation({
    mutationFn: ({ id, emoji }: { id: string; emoji: string }) =>
      toggleReactionApi(orgId, id, emoji),
    onSuccess: () => invalidate(),
    onError: (e) => error('Could not react', extractApiErrorMessage(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!draft.trim() || create.isPending) return;
    create.mutate();
  };

  const comments = commentsQuery.data?.items ?? [];
  const timelogs = timelogsQuery.data?.items ?? [];

  // A small synthetic history: creation + last update. (Server does not expose
  // a full audit trail to the client yet — this keeps the tab meaningful.)
  const historyItems: ActivityItem[] = (() => {
    const items: ActivityItem[] = [];
    items.push({ kind: 'history', at: issue.createdAt, label: 'Issue created' });
    if (issue.updatedAt !== issue.createdAt) {
      items.push({ kind: 'history', at: issue.updatedAt, label: 'Issue last updated' });
    }
    return items;
  })();

  const allItems: ActivityItem[] = [
    ...comments.map<ActivityItem>((c) => ({ kind: 'comment', at: c.createdAt, comment: c })),
    ...timelogs.map<ActivityItem>((t) => ({ kind: 'worklog', at: t.loggedAt, entry: t })),
    ...historyItems,
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  const tabs: Array<{ id: ActivityTab; label: string; count?: number }> = [
    { id: 'all', label: 'All', count: allItems.length },
    { id: 'comments', label: 'Comments', count: comments.length },
    { id: 'history', label: 'History' },
    { id: 'worklog', label: 'Work log', count: timelogs.length },
  ];

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <SectionHeading icon={MessageSquare} label="Activity" />
      </div>

      {/* Tab strip */}
      <div className="flex flex-wrap items-center gap-1 border-b">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            aria-pressed={tab === t.id}
            className={cn(
              '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              tab === t.id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
            {t.count != null && t.count > 0 ? (
              <span
                className={cn(
                  'inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums',
                  tab === t.id ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                )}
              >
                {t.count}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Composer (only on All / Comments tabs) */}
      {canComment && (tab === 'comments' || tab === 'all') ? (
        <form onSubmit={submit} className="space-y-2" aria-label="Add a comment">
          {!composerOpen && !draft.trim() ? (
            <button
              type="button"
              onClick={() => setComposerOpen(true)}
              className="w-full rounded-lg border border-dashed bg-muted/20 px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
            >
              Add a comment…
            </button>
          ) : (
            <>
              <div className="relative rounded-lg border bg-background shadow-sm focus-within:ring-2 focus-within:ring-ring">
                <textarea
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Add a comment… Markdown supported."
                  rows={2}
                  maxLength={10_000}
                  className="w-full resize-y rounded-lg bg-background p-3 pr-20 text-sm focus:outline-none"
                />
                <span className="pointer-events-none absolute bottom-2 right-2 text-[10px] tabular-nums text-muted-foreground">
                  {draft.length}/10000
                </span>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDraft('');
                    setComposerOpen(false);
                  }}
                  disabled={create.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={create.isPending || !draft.trim()}
                  className="gap-1.5"
                >
                  {create.isPending ? (
                    <>
                      <CircleDashed className="h-3.5 w-3.5 animate-spin" aria-hidden /> Posting…
                    </>
                  ) : (
                    <>
                      <Send className="h-3.5 w-3.5" aria-hidden /> Save
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </form>
      ) : null}

      {/* Tab body */}
      {tab === 'all' ? (
        <ActivityFeed
          items={allItems}
          currentUserId={currentUserId}
          editing={editing}
          onStartEdit={(id, body) => setEditing({ id, body })}
          onCancelEdit={() => setEditing(null)}
          onChangeEdit={(v) => setEditing((prev) => (prev ? { ...prev, body: v } : prev))}
          onSaveEdit={() => editing && update.mutate(editing)}
          onDelete={(id) => remove.mutate(id)}
          onReact={(id, emoji) => react.mutate({ id, emoji })}
          saving={update.isPending}
          loading={commentsQuery.isLoading || timelogsQuery.isLoading}
        />
      ) : tab === 'comments' ? (
        <CommentsList
          comments={comments}
          currentUserId={currentUserId}
          editing={editing}
          onStartEdit={(id, body) => setEditing({ id, body })}
          onCancelEdit={() => setEditing(null)}
          onChangeEdit={(v) => setEditing((prev) => (prev ? { ...prev, body: v } : prev))}
          onSaveEdit={() => editing && update.mutate(editing)}
          onDelete={(id) => remove.mutate(id)}
          onReact={(id, emoji) => react.mutate({ id, emoji })}
          saving={update.isPending}
          canComment={canComment}
          loading={commentsQuery.isLoading}
        />
      ) : tab === 'history' ? (
        <HistoryList items={historyItems} />
      ) : (
        <WorkLogList entries={timelogs} loading={timelogsQuery.isLoading} />
      )}
    </section>
  );
}

function CommentsList({
  comments,
  currentUserId,
  editing,
  onStartEdit,
  onCancelEdit,
  onChangeEdit,
  onSaveEdit,
  onDelete,
  onReact,
  saving,
  canComment,
  loading,
}: {
  comments: CommentRow[];
  currentUserId: string | null;
  editing: { id: string; body: string } | null;
  onStartEdit: (id: string, body: string) => void;
  onCancelEdit: () => void;
  onChangeEdit: (v: string) => void;
  onSaveEdit: () => void;
  onDelete: (id: string) => void;
  onReact: (id: string, emoji: string) => void;
  saving: boolean;
  canComment: boolean;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }
  if (comments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed bg-muted/20 py-6 text-center">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-background ring-1 ring-border">
          <MessageSquare className="h-4 w-4 text-muted-foreground" aria-hidden />
        </div>
        <p className="text-xs font-medium">No comments yet</p>
        <p className="text-[11px] text-muted-foreground">
          {canComment ? 'Be the first to add one.' : 'Only members with permission can comment.'}
        </p>
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {comments.map((c) => (
        <li key={c.id}>
          <CommentBubble
            comment={c}
            currentUserId={currentUserId}
            editing={editing?.id === c.id}
            editBody={editing?.id === c.id ? editing.body : ''}
            onStartEdit={() => onStartEdit(c.id, c.bodyMarkdown)}
            onCancelEdit={onCancelEdit}
            onChangeEdit={onChangeEdit}
            onSaveEdit={onSaveEdit}
            onDelete={() => onDelete(c.id)}
            onReact={(emoji) => onReact(c.id, emoji)}
            saving={saving}
          />
        </li>
      ))}
    </ul>
  );
}

function WorkLogList({
  entries,
  loading,
}: {
  entries: TimeLogRow[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed bg-muted/20 py-6 text-center">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-background ring-1 ring-border">
          <Hourglass className="h-4 w-4 text-muted-foreground" aria-hidden />
        </div>
        <p className="text-xs font-medium">No time logged yet</p>
        <p className="text-[11px] text-muted-foreground">
          Log time from the Details section above.
        </p>
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {entries.map((entry) => (
        <li key={entry.id} className="flex items-start gap-3">
          <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold uppercase text-primary ring-1 ring-primary/20">
            {initialsFrom(entry.userName, '')}
          </span>
          <div className="min-w-0 flex-1 rounded-xl border bg-background p-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-semibold">{entry.userName}</span>
              <span className="text-[11px] text-muted-foreground">logged</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-500/30 dark:text-emerald-300">
                <Hourglass className="h-3 w-3" aria-hidden /> {formatMinutes(entry.minutes)}
              </span>
              <span className="text-[11px] text-muted-foreground">
                on {new Date(entry.loggedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
            {entry.description ? (
              <p className="mt-1 text-xs text-muted-foreground">{entry.description}</p>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function HistoryList({ items }: { items: ActivityItem[] }) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed bg-muted/20 py-6 text-center">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-background ring-1 ring-border">
          <Clock className="h-4 w-4 text-muted-foreground" aria-hidden />
        </div>
        <p className="text-xs font-medium">No history yet</p>
      </div>
    );
  }
  return (
    <ul className="space-y-1.5">
      {items.map((item, idx) => (
        <li
          key={idx}
          className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-xs"
        >
          <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="flex-1">{item.kind === 'history' ? item.label : ''}</span>
          <span className="text-[11px] text-muted-foreground">
            {new Date(item.at).toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ActivityFeed({
  items,
  currentUserId,
  editing,
  onStartEdit,
  onCancelEdit,
  onChangeEdit,
  onSaveEdit,
  onDelete,
  onReact,
  saving,
  loading,
}: {
  items: ActivityItem[];
  currentUserId: string | null;
  editing: { id: string; body: string } | null;
  onStartEdit: (id: string, body: string) => void;
  onCancelEdit: () => void;
  onChangeEdit: (v: string) => void;
  onSaveEdit: () => void;
  onDelete: (id: string) => void;
  onReact: (id: string, emoji: string) => void;
  saving: boolean;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed bg-muted/20 py-6 text-center">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-background ring-1 ring-border">
          <MessageSquare className="h-4 w-4 text-muted-foreground" aria-hidden />
        </div>
        <p className="text-xs font-medium">Nothing happening here yet</p>
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {items.map((item, idx) => {
        if (item.kind === 'comment') {
          const c = item.comment;
          return (
            <li key={`c-${c.id}`}>
              <CommentBubble
                comment={c}
                currentUserId={currentUserId}
                editing={editing?.id === c.id}
                editBody={editing?.id === c.id ? editing.body : ''}
                onStartEdit={() => onStartEdit(c.id, c.bodyMarkdown)}
                onCancelEdit={onCancelEdit}
                onChangeEdit={onChangeEdit}
                onSaveEdit={onSaveEdit}
                onDelete={() => onDelete(c.id)}
                onReact={(emoji) => onReact(c.id, emoji)}
                saving={saving}
              />
            </li>
          );
        }
        if (item.kind === 'worklog') {
          const entry = item.entry;
          return (
            <li
              key={`w-${entry.id}`}
              className="flex items-center gap-2 rounded-md border bg-muted/20 px-3 py-2 text-xs"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold uppercase text-primary ring-1 ring-primary/20">
                {initialsFrom(entry.userName, '')}
              </span>
              <span className="font-medium">{entry.userName}</span>
              <span className="text-muted-foreground">logged</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-500/30 dark:text-emerald-300">
                <Hourglass className="h-2.5 w-2.5" aria-hidden /> {formatMinutes(entry.minutes)}
              </span>
              <span className="ml-auto text-[11px] text-muted-foreground">
                {new Date(entry.loggedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </span>
            </li>
          );
        }
        return (
          <li
            key={`h-${idx}`}
            className="flex items-center gap-2 rounded-md border bg-muted/10 px-3 py-2 text-xs"
          >
            <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <span className="flex-1">{item.label}</span>
            <span className="text-[11px] text-muted-foreground">
              {new Date(item.at).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function CommentBubble({
  comment,
  currentUserId,
  editing,
  editBody,
  onStartEdit,
  onCancelEdit,
  onChangeEdit,
  onSaveEdit,
  onDelete,
  onReact,
  saving,
}: {
  comment: CommentRow;
  currentUserId: string | null;
  editing: boolean;
  editBody: string;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onChangeEdit: (value: string) => void;
  onSaveEdit: () => void;
  onDelete: () => void;
  onReact: (emoji: string) => void;
  saving: boolean;
}) {
  const initials = initialsFrom(comment.authorName, comment.authorEmail);
  const created = new Date(comment.createdAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const edited = comment.updatedAt !== comment.createdAt;

  if (comment.deletedAt) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/20 p-3 text-xs italic text-muted-foreground">
        Comment was deleted.
      </div>
    );
  }

  return (
    <div className="group flex gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold uppercase text-primary ring-1 ring-primary/20">
        {initials}
      </span>
      <div className="min-w-0 flex-1 rounded-xl border bg-background p-3 shadow-sm transition-shadow hover:shadow-md">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold">{comment.authorName}</span>
          <span className="text-[11px] text-muted-foreground">{created}</span>
          {edited ? (
            <span className="text-[10px] italic text-muted-foreground">(edited)</span>
          ) : null}
          {(comment.canEdit || comment.canDelete) && !editing ? (
            <div className="ml-auto flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              {comment.canEdit ? (
                <button
                  type="button"
                  onClick={onStartEdit}
                  className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  title="Edit comment"
                  aria-label="Edit comment"
                >
                  <Pencil className="h-3 w-3" aria-hidden />
                </button>
              ) : null}
              {comment.canDelete ? (
                <button
                  type="button"
                  onClick={onDelete}
                  className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-rose-500/10 hover:text-rose-600"
                  title="Delete comment"
                  aria-label="Delete comment"
                >
                  <Trash2 className="h-3 w-3" aria-hidden />
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        {editing ? (
          <div className="mt-2 space-y-2">
            <textarea
              value={editBody}
              onChange={(e) => onChangeEdit(e.target.value)}
              rows={3}
              maxLength={10_000}
              className="w-full resize-y rounded-lg border border-input bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={onCancelEdit}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={onSaveEdit}
                disabled={saving || !editBody.trim()}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-1.5 space-y-2">
            <Markdown
              source={comment.bodyMarkdown}
              className="prose-sm max-w-none text-sm dark:prose-invert"
            />
            <div className="flex flex-wrap items-center gap-1">
              {comment.reactions.map((r) => {
                const reacted = currentUserId != null && r.userIds.includes(currentUserId);
                return (
                  <button
                    key={r.emoji}
                    type="button"
                    onClick={() => onReact(r.emoji)}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-all',
                      reacted
                        ? 'bg-primary/10 text-primary ring-1 ring-primary/30'
                        : 'bg-muted text-muted-foreground ring-1 ring-border hover:bg-muted/80',
                    )}
                  >
                    <span>{REACTION_DISPLAY[r.emoji] ?? r.emoji}</span>
                    <span className="tabular-nums">{r.count}</span>
                  </button>
                );
              })}
              <ReactionPicker onPick={onReact} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReactionPicker({ onPick }: { onPick: (emoji: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground opacity-0 transition-all hover:bg-muted hover:text-foreground group-hover:opacity-100"
        aria-label="Add reaction"
        title="Add reaction"
      >
        <Smile className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-10"
            aria-hidden
            onClick={() => setOpen(false)}
          />
          <div className="absolute bottom-full left-0 z-20 mb-1 flex gap-1 rounded-lg border bg-popover p-1 shadow-lg">
            {REACTIONS.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => {
                  onPick(e);
                  setOpen(false);
                }}
                className="flex h-7 w-7 items-center justify-center rounded-md text-lg transition-colors hover:bg-muted"
                title={e}
              >
                {REACTION_DISPLAY[e] ?? e}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

// -------------------- Shared bits --------------------

function SectionHeading({
  icon: Icon,
  label,
}: {
  icon: typeof FileText;
  label: string;
}) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      <Icon className="h-3.5 w-3.5" aria-hidden /> {label}
    </div>
  );
}

function DetailRow({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon: typeof FileText;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 transition-colors hover:bg-muted/20">
      <div className="flex w-32 shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" aria-hidden />
        <span className="font-medium">{label}</span>
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function toISODate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
